import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { ChevronLeft, ChevronRight, Clock, MapPin, MountainSnow, Route, X } from "lucide-react";
import { dictionaries } from "../lib/i18n";
import { formatHours } from "../lib/format";
import type { CorridorOption, UiLang, Stop } from "../lib/types";

interface Props {
  corridor: CorridorOption;
  /** Stoppene langs ruta. Tom mens de hentes. */
  stops: Stop[];
  lang: UiLang;
  onClose: () => void;
  /** Bekrefter valget og lukker — «planlegg denne ruta». */
  onPlan: () => void;
}

/** Hvor mange steder reisen fortelles med. Nok til å vise spennet, få nok til å huskes. */
const SLIDES = 8;

/** Millisekunder per bilde. UiLang nok til å lese navnet og se bildet. */
const DWELL_MS = 4000;

/**
 * Presentasjonen av en rute, for noen som aldri har vært i Norge.
 *
 * Kortene i rutevelgeren sier «Dombås → Ålesund, 13 stopp, 10 timer». For en
 * nordmann er det nok; for en tilreisende er det tre stedsnavn uten innhold.
 * Ingen vet hva Trollstigen er før de har sett den, og appen hadde bildet av
 * hvert eneste stopp liggende ubrukt bak et klikk på et kart de ennå ikke hadde
 * grunn til å åpne.
 *
 * Her fortelles ruta som den reises: stoppene i geografisk rekkefølge, ett bilde
 * om gangen, med stedets egen beskrivelse under. Åtte bilder er nok til å vise
 * spennet fra fjord til fjell uten å bli et arkiv.
 *
 * Utvalget er jevnt fordelt langs ruta framfor å være de åtte første. Det er
 * samme grunn som `sampleEvenly` i ruteplanleggeren finnes for: de første åtte
 * stoppene på Bergensbanen er Østlandet, og en presentasjon som stopper i
 * Hallingdal selger ikke en tur til Bergen.
 */
export function CorridorShowcase({ corridor, stops, lang, onClose, onPlan }: Props) {
  const t = dictionaries[lang];
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);

  /*
   * Stoppene reisen fortelles med: i rekkefølge, bare de med bilde, jevnt
   * fordelt. Uten bilde har lysbildet ingenting å vise.
   */
  const slides = useMemo(() => {
    const withPhoto = [...stops]
      .filter((stop) => stop.image)
      .sort((a, b) => a.position - b.position);

    if (withPhoto.length <= SLIDES) return withPhoto;

    const step = (withPhoto.length - 1) / (SLIDES - 1);
    return Array.from({ length: SLIDES }, (_, i) => withPhoto[Math.round(i * step)]);
  }, [stops]);

  const count = slides.length;
  const go = useCallback((next: number) => setIndex(((next % count) + count) % count), [count]);

  // Ny rute: begynn forfra, ellers står man på bilde seks av en annen reise.
  useEffect(() => setIndex(0), [corridor.id]);

  /*
   * Bildene skifter av seg selv, men ikke for den som har bedt om mindre
   * bevegelse — en presentasjon som beveger seg uoppfordret er nettopp det
   * `prefers-reduced-motion` finnes for.
   */
  const reduced = useRef(false);
  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    reduced.current = media.matches;
    const update = () => (reduced.current = media.matches);
    media.addEventListener("change", update);
    return () => media.removeEventListener("change", update);
  }, []);

  useEffect(() => {
    if (paused || count < 2 || reduced.current) return;
    const timer = window.setTimeout(() => go(index + 1), DWELL_MS);
    return () => window.clearTimeout(timer);
  }, [index, paused, count, go]);

  // Esc lukker, piltastene bytter bilde.
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
      if (event.key === "ArrowRight") go(index + 1);
      if (event.key === "ArrowLeft") go(index - 1);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [index, go, onClose]);

  const current = slides[index];

  return (
    <div
      className="showcase-backdrop no-print"
      role="dialog"
      aria-modal="true"
      aria-label={`${corridor.startName} – ${corridor.endName}`}
      onClick={onClose}
    >
      {/*
        Pausen ligger på teksten og foten, ikke på hele dialogen.

        Den satt på `.showcase` og gjorde bildeskiftet dødt: dialogen dekker
        nesten hele skjermen, og man åpner den ved å klikke — så pekeren står
        alltid inne i den i det den vises. `paused` ble sann med én gang og ble
        aldri usann igjen, og presentasjonen sto på bilde én til noen fant
        pilene. Nå pauser den der man faktisk leser eller velger.
      */}
      <div className="showcase" onClick={(event) => event.stopPropagation()}>
        <button className="showcase-close" onClick={onClose} aria-label={t.close}>
          <X size={20} />
        </button>

        <div className="showcase-stage">
          {/*
            Alle bildene ligger i DOM-en og krysstoner med opacity. Å bytte
            `src` ga et hvitt glimt mens det neste lastet — og et lysbildeskift
            som blinker ser ut som en feil, ikke som en overgang.
          */}
          {slides.map((stop, i) => (
            <img
              key={stop.id}
              className={`showcase-photo ${i === index ? "showcase-photo--on" : ""}`}
              /*
               * Den store utgaven her, ikke `src`. `src` er 640 px, valgt for
               * bildekortet på kartet; scenen er inntil 1080 px og zoomer til
               * 1,09, så bildet ble strukket nesten dobbelt og var synlig
               * uskarpt. `wide` mangler for de få stoppene der originalen på
               * Commons er for liten, og da er 640 px fortsatt bedre enn et hull.
               */
              src={stop.image!.wide ?? stop.image!.src}
              alt={stop.name}
              // Bare det første bildet haster; resten hentes mens man ser.
              loading={i === 0 ? "eager" : "lazy"}
              // Naboene hentes med forrang, så et pilklikk ikke møter et tomt felt.
              fetchPriority={i === index ? "high" : i === index + 1 ? "auto" : "low"}
              decoding="async"
              aria-hidden={i !== index}
            />
          ))}

          <span className="showcase-scrim" />

          {/*
            Framdriften mot neste bilde.

            Uten den er det uforklarlig at bildet plutselig skifter mens man
            leser — og man vet ikke om det er verdt å vente eller om man skal
            trykke videre selv. Streken animeres med `transform`, som går på
            kompositoren og aldri tvinger fram en ny layout.

            `key` gjør at animasjonen starter forfra på hvert bilde. Den vises
            ikke når presentasjonen står stille, for da er det ingenting å vente på.
          */}
          {count > 1 && !paused && (
            <span className="showcase-progress" key={`${index}-${paused}`} aria-hidden="true">
              <i style={{ animationDuration: `${DWELL_MS}ms` }} />
            </span>
          )}

          {count > 1 && (
            <>
              <button
                className="showcase-arrow showcase-arrow--prev"
                onClick={() => go(index - 1)}
                aria-label={t.showcasePrevious}
              >
                <ChevronLeft size={22} />
              </button>
              <button
                className="showcase-arrow showcase-arrow--next"
                onClick={() => go(index + 1)}
                aria-label={t.showcaseNext}
              >
                <ChevronRight size={22} />
              </button>
            </>
          )}

          <div
            className="showcase-caption"
            onMouseEnter={() => setPaused(true)}
            onMouseLeave={() => setPaused(false)}
          >
            <span className="showcase-eyebrow">{t.showcaseEyebrow}</span>
            <h2>
              {corridor.startName} <span className="showcase-arrow-text">→</span>{" "}
              {corridor.endName}
            </h2>
            <p className="showcase-tagline">{corridor.tagline}</p>

            {current && (
              <div className="showcase-stop" key={current.id}>
                <strong>
                  <MapPin size={13} /> {current.name}
                </strong>
                <p>{current.blurb}</p>
                {current.image && (
                  <small>
                    {t.photoBy} {current.image.credit} · {current.image.license}
                  </small>
                )}
              </div>
            )}
          </div>
        </div>

        <div
          className="showcase-foot"
          onMouseEnter={() => setPaused(true)}
          onMouseLeave={() => setPaused(false)}
        >
          <ul className="showcase-facts">
            <li>
              <MapPin size={13} />
              {t.stopCount(corridor.stopCount)}
            </li>
            <li>
              <Clock size={13} />
              {t.endToEnd(formatHours(Math.round(corridor.spanHours), lang))}
            </li>
            <li>
              <Route size={13} />
              {corridor.modes.map((mode) => t.travelModeName[mode]).join(" · ")}
            </li>
            {highestOf(slides) > 0 && (
              <li>
                <MountainSnow size={13} />
                {highestOf(slides)} {t.unitMasl}
              </li>
            )}
          </ul>

          {/* Miniatyrene gjør presentasjonen til noe man kan bla i, ikke bare se på. */}
          {count > 1 && (
            <div className="showcase-strip">
              {slides.map((stop, i) => (
                <button
                  key={stop.id}
                  className={`showcase-thumb ${i === index ? "showcase-thumb--on" : ""}`}
                  onClick={() => go(i)}
                  aria-label={stop.name}
                  aria-current={i === index}
                >
                  <img src={stop.image!.src} alt="" loading="lazy" aria-hidden="true" />
                </button>
              ))}
            </div>
          )}

          {/* Samme hovedknapp som «lag reiseplan» — det er samme handling. */}
          <button className="generate showcase-plan" onClick={onPlan}>
            {t.showcasePlan}
          </button>
        </div>
      </div>
    </div>
  );
}

/** Høyeste punkt blant bildene som vises. Sier om ruta går i fjellet. */
function highestOf(stops: Stop[]): number {
  return stops.reduce((max, stop) => Math.max(max, stop.elevationM), 0);
}
